// Pages V3: Accommodations · Directories · Change password
import { useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useMember, formatErr } from "@/contexts/MemberAuthContext";
import PasswordInput from "@/components/forms/PasswordInput";

const HERO = "/photos/prieure-meadow.jpg";

function Shell({ title, kicker, children }) {
  return (
    <div data-testid="espace-v3">
      <section className="relative" style={{ height: "30vh", minHeight: 220 }}>
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,16,12,0.55), rgba(20,16,12,0.4))" }} />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-8">
          <Link to="/espace" className="font-caps text-[0.65rem] text-white/80" style={{ letterSpacing: "0.25em" }}>← MON ESPACE</Link>
          {kicker && <div className="font-caps text-[0.7rem] mt-3" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>{kicker}</div>}
          <h1 className="font-display text-4xl sm:text-5xl text-white mt-2">{title}</h1>
        </div>
      </section>
      <div className="py-12">{children}</div>
    </div>
  );
}
function Guard({ children, allow }) {
  const { member } = useMember();
  if (member === null) return <Shell title="…"><p className="text-center">Chargement…</p></Shell>;
  if (!member) return <Navigate to="/espace/connexion" replace />;
  if (allow && !allow.includes(member.role)) return <Navigate to="/espace" replace />;
  return children;
}

// ============================ ACCOMMODATIONS
export function EspaceHebergements() {
  const { espace, member } = useMember();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "hotel", address: "", phone: "", website: "", distance_km: "", price_range: "", notes: "" });
  const isOrg = member?.role === "organisateur";
  const reload = () => espace.get("/espace/accommodations").then(r => setItems(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, distance_km: form.distance_km ? parseFloat(form.distance_km) : null };
    await espace.post("/espace/accommodations", payload);
    setForm({ name: "", kind: "hotel", address: "", phone: "", website: "", distance_km: "", price_range: "", notes: "" });
    reload();
  };
  const remove = async (id) => { if (window.confirm("Supprimer ?")) { await espace.delete(`/espace/accommodations/${id}`); reload(); } };
  return (
    <Guard>
      <Shell title="Hébergements" kicker="OÙ DORMIR PRÈS DU PRIEURÉ">
        <div className="container-prieure max-w-4xl space-y-6">
          {isOrg && (
            <form onSubmit={submit} className="bg-white border p-6 grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ borderColor: "var(--border)" }}>
              <h3 className="font-display text-xl sm:col-span-2">Ajouter un hébergement</h3>
              <input required placeholder="Nom" value={form.name} onChange={(e)=>setForm(s=>({...s,name:e.target.value}))} className="input-classic" />
              <select value={form.kind} onChange={(e)=>setForm(s=>({...s,kind:e.target.value}))} className="input-classic">
                <option value="hotel">Hôtel</option>
                <option value="guesthouse">Maison d'hôtes</option>
                <option value="camping">Camping</option>
                <option value="other">Autre</option>
              </select>
              <input placeholder="Adresse" value={form.address} onChange={(e)=>setForm(s=>({...s,address:e.target.value}))} className="input-classic sm:col-span-2" />
              <input placeholder="Téléphone" value={form.phone} onChange={(e)=>setForm(s=>({...s,phone:e.target.value}))} className="input-classic" />
              <input placeholder="Site web" value={form.website} onChange={(e)=>setForm(s=>({...s,website:e.target.value}))} className="input-classic" />
              <input type="number" step="0.1" placeholder="Distance (km)" value={form.distance_km} onChange={(e)=>setForm(s=>({...s,distance_km:e.target.value}))} className="input-classic" />
              <input placeholder="Tarif (ex: 70-120€)" value={form.price_range} onChange={(e)=>setForm(s=>({...s,price_range:e.target.value}))} className="input-classic" />
              <textarea rows={2} placeholder="Notes" value={form.notes} onChange={(e)=>setForm(s=>({...s,notes:e.target.value}))} className="input-classic sm:col-span-2" />
              <button className="btn btn-primary sm:col-span-2">Publier</button>
            </form>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.length === 0 && <p className="md:col-span-2 text-center" style={{ color: "var(--muted)" }}>Aucun hébergement listé pour l'instant.</p>}
            {items.map(a => (
              <article key={a.id} className="bg-white border p-5" style={{ borderColor: "var(--border)", borderLeftWidth: 3, borderLeftColor: "var(--gold)" }}>
                <div className="flex justify-between items-start mb-1">
                  <span className="font-caps text-[0.6rem]" style={{ color: "var(--cinnabar)", letterSpacing: "0.2em" }}>{(a.kind || "").toUpperCase()}</span>
                  {a.distance_km != null && <span className="text-xs" style={{ color: "var(--muted)" }}>{a.distance_km} km</span>}
                </div>
                <h4 className="font-display text-xl">{a.name}</h4>
                {a.address && <p className="text-sm mt-1">{a.address}</p>}
                <div className="text-sm mt-2 space-y-0.5" style={{ color: "var(--ink-2)" }}>
                  {a.phone && <div>📞 <a href={`tel:${a.phone}`} className="link-underline">{a.phone}</a></div>}
                  {a.website && <div>🌐 <a href={a.website} target="_blank" rel="noopener noreferrer" className="link-underline">{a.website.replace(/^https?:\/\//, "")}</a></div>}
                  {a.price_range && <div>💶 {a.price_range}</div>}
                </div>
                {a.notes && <p className="text-sm mt-2 italic" style={{ color: "var(--ink-2)" }}>{a.notes}</p>}
                {isOrg && <button onClick={() => remove(a.id)} className="text-xs link-underline mt-2" style={{ color: "var(--cinnabar)" }}>Supprimer</button>}
              </article>
            ))}
          </div>
        </div>
      </Shell>
    </Guard>
  );
}

// ============================ DIRECTORY (organisateur only)
export function EspaceAnnuaire() {
  const { espace } = useMember();
  const { role } = useParams();
  const [items, setItems] = useState([]);
  const labels = { participant: "Participants", benevole: "Bénévoles", organisateur: "Organisateurs" };
  useEffect(() => { espace.get(`/espace/directory/${role}`).then(r => setItems(r.data)).catch(() => setItems([])); }, [espace, role]);
  return (
    <Guard allow={["organisateur"]}>
      <Shell title={`Annuaire — ${labels[role] || role}`} kicker="LISTE DES MEMBRES">
        <div className="container-prieure">
          <div className="flex gap-2 mb-6" data-testid="annuaire-tabs">
            {Object.entries(labels).map(([k, v]) => (
              <Link key={k} to={`/espace/annuaire/${k}`} className="font-caps text-[0.7rem] px-4 py-2 border" style={{ background: k === role ? "var(--cinnabar)" : "white", color: k === role ? "white" : "var(--ink)", borderColor: k === role ? "var(--cinnabar)" : "var(--border)", letterSpacing: "0.18em" }}>{v.toUpperCase()}</Link>
            ))}
          </div>
          <div className="bg-white border overflow-x-auto" style={{ borderColor: "var(--border)" }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                  <th className="text-left p-3 label-caps">Nom</th>
                  <th className="text-left p-3 label-caps">Email</th>
                  <th className="text-left p-3 label-caps">Téléphone</th>
                  <th className="text-left p-3 label-caps">Ville · Pays</th>
                  <th className="text-left p-3 label-caps">Association</th>
                </tr>
              </thead>
              <tbody>
                {items.map(m => (
                  <tr key={m.id} className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="p-3">{m.name}</td>
                    <td className="p-3"><a href={`mailto:${m.email}`} className="link-underline">{m.email}</a></td>
                    <td className="p-3">{m.phone || "—"}</td>
                    <td className="p-3">{[m.city, m.country].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="p-3">{m.organization || "—"}</td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan="5" className="p-6 text-center" style={{ color: "var(--muted)" }}>Aucun membre dans cette catégorie.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </Shell>
    </Guard>
  );
}

// ============================ CHANGE PASSWORD
export function EspaceMotDePasse() {
  const { espace, refresh } = useMember();
  const [params] = useSearchParams();
  const forced = params.get("force") === "1";
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setMsg("");
    if (form.new_password !== form.confirm) { setMsg("Les deux mots de passe ne correspondent pas."); return; }
    if (form.new_password.length < 8) { setMsg("Mot de passe trop court (8 caractères minimum)."); return; }
    setBusy(true);
    try {
      await espace.post("/espace/change-password", { current_password: form.current_password, new_password: form.new_password });
      setMsg("✓ Mot de passe modifié avec succès.");
      setForm({ current_password: "", new_password: "", confirm: "" });
      // Refresh member state so the must_change_password flag is cleared in the UI
      if (refresh) await refresh();
    } catch (ex) { setMsg(formatErr(ex)); } finally { setBusy(false); }
  };
  return (
    <Guard>
      <Shell title="Changer mon mot de passe" kicker="SÉCURITÉ DU COMPTE">
        <div className="container-prieure max-w-md">
          {forced && (
            <div data-testid="force-pw-banner" className="border p-4 mb-4"
                 style={{ borderColor: "var(--cinnabar)", background: "rgba(153,27,27,0.08)", borderLeftWidth: 3 }}>
              <div className="font-caps text-[0.65rem] mb-1" style={{ color: "var(--cinnabar)", letterSpacing: "0.22em" }}>ACTION REQUISE</div>
              <p className="font-body text-sm" style={{ color: "var(--ink)" }}>
                Votre mot de passe vient d'être réinitialisé par un administrateur.
                Veuillez en choisir un nouveau pour continuer à utiliser votre espace.
              </p>
            </div>
          )}
          <form onSubmit={submit} data-testid="pw-form" className="bg-white border p-8 space-y-4" style={{ borderColor: "var(--border)" }}>
            <label className="block">
              <span className="label-caps">MOT DE PASSE ACTUEL</span>
              <div className="mt-1">
                <PasswordInput testId="pw-current" required value={form.current_password}
                               onChange={(e)=>setForm(s=>({...s,current_password:e.target.value}))}
                               autoComplete="current-password" />
              </div>
            </label>
            <label className="block">
              <span className="label-caps">NOUVEAU MOT DE PASSE</span>
              <div className="mt-1">
                <PasswordInput testId="pw-new" required minLength={8} value={form.new_password}
                               onChange={(e)=>setForm(s=>({...s,new_password:e.target.value}))}
                               autoComplete="new-password" />
              </div>
            </label>
            <label className="block">
              <span className="label-caps">CONFIRMER</span>
              <div className="mt-1">
                <PasswordInput testId="pw-confirm" required minLength={8} value={form.confirm}
                               onChange={(e)=>setForm(s=>({...s,confirm:e.target.value}))}
                               autoComplete="new-password" />
              </div>
            </label>
            {msg && <p className="text-sm" style={{ color: msg.startsWith("✓") ? "var(--moss)" : "var(--cinnabar)" }}>{msg}</p>}
            <button type="submit" disabled={busy} className="btn btn-primary w-full">{busy ? "..." : "Modifier"}</button>
            <Link to="/espace" className="block text-center link-underline text-sm" style={{ color: "var(--ink-2)" }}>Retour au dashboard</Link>
          </form>
        </div>
      </Shell>
    </Guard>
  );
}
